import { useCallback, useEffect, useState } from 'react';
import * as Sentry from '@sentry/react';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { getApiErrorMessage } from '../lib/apiErrors';
import { useAuthContext } from '../contexts/AuthContext';
import { useProductTourContext } from '../contexts/ProductTourContext';
import { usePracticeWalkthroughContext } from '../contexts/PracticeWalkthroughContext';
import { useTeamDashboardWalkthroughContext } from '../contexts/TeamDashboardWalkthroughContext';
import { parsePracticeSessionId } from '../practiceWalkthrough/parsePracticeSessionId';
import {
  STORAGE_PENDING_NEW_MANUAL,
  STORAGE_PENDING_SESSION_MANUAL,
} from '../practiceWalkthrough/storageKeys';
import { STORAGE_PENDING_TEAM_MANUAL } from '../teamWalkthrough/storageKeys';
import { isOrgManager } from '../lib/orgRoles';
import { PracticeTimeLimitControl } from '../components/practice/PracticeTimeLimitControl';
import { useAutosave, type AutosaveStatus } from '../hooks/useAutosave';

function AutosaveIndicator({ status, error }: { status: AutosaveStatus; error: string | null }) {
  if (status === 'saving')
    return <p className="text-sm text-gray-500 mt-2 transition-opacity">Saving…</p>;
  if (status === 'saved')
    return (
      <p className="text-sm text-green-700 mt-2 transition-opacity" role="status">
        Saved
      </p>
    );
  if (status === 'error')
    return (
      <p className="text-sm text-red-600 mt-2" role="alert">
        {error}
      </p>
    );
  return null;
}

export function ProfilePage() {
  const { user } = useAuthContext();
  const navigate = useNavigate();
  const location = useLocation();
  const { requestManualTour } = useProductTourContext();
  const { requestManualNewPracticeWalkthrough, requestManualPracticeSessionWalkthrough } =
    usePracticeWalkthroughContext();
  const { requestManualTeamDashboardWalkthrough } = useTeamDashboardWalkthroughContext();
  const canViewTeamWalkthrough = isOrgManager(user?.orgRole);
  const [maxMinutes, setMaxMinutes] = useState(50);
  const [minutes, setMinutes] = useState(15);
  const [showTranscriptByDefault, setShowTranscriptByDefault] = useState(false);
  const [showPersonaFaces, setShowPersonaFaces] = useState(true);
  const [loaded, setLoaded] = useState(false);

  const loadPrefs = useCallback(async () => {
    try {
      const res = await api.get('/orgs/me');
      const max = res.data.maxPracticeTimeLimitMinutes ?? 50;
      const pref = res.data.preferredPracticeTimeLimitMinutes;
      setMaxMinutes(max);
      if (pref != null) {
        setMinutes(Math.min(max, Math.max(1, pref)));
      } else {
        setMinutes(15);
      }
      setShowTranscriptByDefault(Boolean(res.data.showTranscriptByDefault));
      setShowPersonaFaces(res.data.showPersonaFaces !== false);
    } catch {
      setMaxMinutes(50);
      setMinutes(15);
      setShowTranscriptByDefault(false);
      setShowPersonaFaces(true);
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    loadPrefs().catch(() => {});
  }, [loadPrefs]);

  const saveTimeLimitFn = useCallback(async (value: number) => {
    try {
      await api.patch('/orgs/me/preferences', { preferredPracticeTimeLimitMinutes: value });
    } catch (err: unknown) {
      Sentry.captureException(err);
      throw new Error(getApiErrorMessage(err));
    }
  }, []);

  const saveTranscriptFn = useCallback(async (value: boolean) => {
    try {
      await api.patch('/orgs/me/preferences', { showTranscriptByDefault: value });
    } catch (err: unknown) {
      Sentry.captureException(err);
      throw new Error(getApiErrorMessage(err));
    }
  }, []);

  const savePersonaFacesFn = useCallback(async (value: boolean) => {
    try {
      await api.patch('/orgs/me/preferences', { showPersonaFaces: value });
    } catch (err: unknown) {
      Sentry.captureException(err);
      throw new Error(getApiErrorMessage(err));
    }
  }, []);

  const timeLimit = useAutosave(minutes, saveTimeLimitFn, {
    delayMs: 800,
    enabled: loaded,
  });

  const transcript = useAutosave(showTranscriptByDefault, saveTranscriptFn, {
    delayMs: 0,
    enabled: loaded,
  });

  const personaFaces = useAutosave(showPersonaFaces, savePersonaFacesFn, {
    delayMs: 0,
    enabled: loaded,
  });

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Profile</h1>
        <p className="text-gray-600 mt-1">Your account in this organization.</p>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-6 max-w-lg space-y-4">
        <div>
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Name</p>
          <p className="text-gray-900 mt-1">{user?.name ?? '—'}</p>
        </div>
        <div>
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Email</p>
          <p className="text-gray-900 mt-1">{user?.email ?? '—'}</p>
        </div>

        {loaded && (
          <div className="pt-2 border-t border-gray-100">
            <h2 className="text-sm font-semibold text-gray-900 mb-1">
              Default practice time limit
            </h2>
            <p className="text-sm text-gray-600 mb-3">
              When you start a new practice, we preselect this duration (up to {maxMinutes} minutes
              for your organization). You can still change it on each session.
            </p>
            <PracticeTimeLimitControl
              idPrefix="profile-time"
              maxMinutes={maxMinutes}
              valueMinutes={minutes}
              onChangeMinutes={setMinutes}
            />
            <AutosaveIndicator status={timeLimit.status} error={timeLimit.error} />
          </div>
        )}

        {loaded && (
          <div className="pt-2 border-t border-gray-100">
            <h2 className="text-sm font-semibold text-gray-900 mb-1">Video Call transcript</h2>
            <p className="text-sm text-gray-600 mb-3">
              When enabled, new video call sessions open the transcript panel on wide screens. On
              smaller screens the panel stays closed until you use the in-session toggle.
            </p>
            <label className="flex items-start gap-2 cursor-pointer text-sm">
              <input
                type="checkbox"
                className="mt-0.5 rounded border-gray-300 text-green-700 focus:ring-green-600"
                checked={showTranscriptByDefault}
                onChange={(e) => setShowTranscriptByDefault(e.target.checked)}
              />
              <span className="text-gray-800">Show transcript on start (wide screens)</span>
            </label>
            <AutosaveIndicator status={transcript.status} error={transcript.error} />
          </div>
        )}

        {loaded && (
          <div className="pt-2 border-t border-gray-100">
            <h2 className="text-sm font-semibold text-gray-900 mb-1">
              Persona images during calls
            </h2>
            <p className="text-sm text-gray-600 mb-3">
              When enabled, video call practice shows AI-generated participant images on persona
              tiles. When disabled, tiles use initials only.
            </p>
            <label className="flex items-start gap-2 cursor-pointer text-sm">
              <input
                type="checkbox"
                className="mt-0.5 rounded border-gray-300 text-green-700 focus:ring-green-600"
                checked={showPersonaFaces}
                onChange={(e) => setShowPersonaFaces(e.target.checked)}
              />
              <span className="text-gray-800">Show persona images on video call tiles</span>
            </label>
            <AutosaveIndicator status={personaFaces.status} error={personaFaces.error} />
          </div>
        )}

        <div className="pt-2 border-t border-gray-100">
          <p className="text-sm text-gray-600 mb-3">
            Take the guided tour again to revisit navigation, personas, and admin tools.
          </p>
          <button
            type="button"
            onClick={() => requestManualTour()}
            className="bg-green-700 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-green-800 transition-colors"
          >
            Take the tour again
          </button>
        </div>

        <div className="pt-2 border-t border-gray-100">
          <h2 className="text-sm font-semibold text-gray-900 mb-1">Practice walkthroughs</h2>
          <p className="text-sm text-gray-600 mb-3">
            Replay the same in-page guides you see the first time you open New Practice or a live
            session. The session tour starts automatically after you open a session from Practice.
          </p>
          <div className="flex flex-col sm:flex-row gap-2">
            <button
              type="button"
              onClick={() => {
                if (location.pathname === '/practice/new') {
                  requestManualNewPracticeWalkthrough();
                } else {
                  try {
                    sessionStorage.setItem(STORAGE_PENDING_NEW_MANUAL, '1');
                  } catch {
                    // ignore
                  }
                  void navigate('/practice/new');
                }
              }}
              className="bg-gray-800 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-gray-900 transition-colors text-center"
            >
              New practice page walkthrough
            </button>
            <button
              type="button"
              onClick={() => {
                if (parsePracticeSessionId(location.pathname)) {
                  requestManualPracticeSessionWalkthrough();
                } else {
                  try {
                    sessionStorage.setItem(STORAGE_PENDING_SESSION_MANUAL, '1');
                  } catch {
                    // ignore
                  }
                  void navigate('/practice');
                }
              }}
              className="bg-gray-800 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-gray-900 transition-colors text-center"
            >
              Practice session walkthrough
            </button>
            {canViewTeamWalkthrough && (
              <button
                type="button"
                onClick={() => {
                  if (location.pathname === '/team') {
                    requestManualTeamDashboardWalkthrough();
                  } else {
                    try {
                      sessionStorage.setItem(STORAGE_PENDING_TEAM_MANUAL, '1');
                    } catch {
                      // ignore
                    }
                    void navigate('/team');
                  }
                }}
                className="bg-gray-800 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-gray-900 transition-colors text-center"
              >
                Team dashboard walkthrough
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
